import { Injectable } from '@angular/core'
import { Observable, of } from 'rxjs'
import { catchError, map, shareReplay, take } from 'rxjs/operators'
import { DetailsService } from '@app/shared/pages/settings/services/details.service'

/**
 * The one timezone the booking screens speak: the CLIENT'S.
 *
 * A room is a physical place in a branch, and every rule PAPI enforces about it — opening hours,
 * the cut-off, which calendar month an allowance period covers — is evaluated against
 * `clients.time_zone`, a whole-hour UTC offset. The portal, left alone, renders in the BROWSER'S
 * zone. For an admin sitting in the same country those agree and nothing looks wrong; for an admin
 * abroad, or on a laptop whose clock is set elsewhere, the same booking reads 05:30 in the grid,
 * 04:30 on the sidebar clock, and is validated by PAPI as though it were 04:30. Three answers for
 * one meeting, and the one the admin types into the picker is the one nobody agrees with.
 *
 * So every instant on these screens is shifted to the client's wall clock on the way in and shifted
 * back on the way out. Storage and the wire stay absolute UTC — this is a display contract only.
 */
@Injectable({ providedIn: 'root' })
export class BookingTimeService {
  /** Whole-hour UTC offset, straight off the central `clients` row — the same value PAPI reads. */
  private offsetHours = 0
  private offset$: Observable<number> | null = null

  constructor(private detailsService: DetailsService) {}

  /**
   * Resolve the client's offset once per page load.
   *
   * Deliberately NOT cached for the lifetime of the app: a portal user can switch client without a
   * page reload, and a cached offset would then quietly date every booking screen by an hour or two
   * with nothing on screen to suggest it. `shareReplay` only collapses the concurrent subscribers
   * of a single load — `reset()` drops it.
   */
  load(): Observable<number> {
    if (!this.offset$) {
      this.offset$ = this.detailsService.getCurrentClientBy('time_zone').pipe(
        take(1),
        map((client) => {
          this.offsetHours = Number((client as { time_zone?: number })?.time_zone ?? 0) || 0
          return this.offsetHours
        }),
        catchError(() => of(this.offsetHours)),
        shareReplay({ bufferSize: 1, refCount: false })
      )
    }
    return this.offset$
  }

  reset(): void {
    this.offset$ = null
  }

  /** "GMT+2" — shown next to the times so the reader knows whose clock they are looking at. */
  get label(): string {
    return `GMT${this.offsetHours >= 0 ? '+' : ''}${this.offsetHours}`
  }

  /** True when the browser and the client disagree, i.e. when saying so is worth the pixels. */
  get differsFromBrowser(): boolean {
    return -new Date().getTimezoneOffset() / 60 !== this.offsetHours
  }

  /**
   * A real UTC instant → a Date whose BROWSER-local clock reads the client's wall time.
   *
   * The returned Date is a lie about the instant and true about the clock face, which is exactly
   * what a display layer wants: every `date` pipe, `getHours()` and PrimeNG picker in this feature
   * then works unchanged and shows the client's time. Never send one of these to the server —
   * `toUtc` is its inverse and the only way back.
   */
  toWall(instant: string | Date | number): Date {
    const utc = new Date(instant)
    return new Date(utc.getTime() + this.offsetHours * 3_600_000 + utc.getTimezoneOffset() * 60_000)
  }

  /**
   * A wall-clock Date the admin picked (or that `toWall` produced) → the real UTC instant.
   *
   * Uses the offset of the wall Date itself rather than of the resulting instant, so what the admin
   * saw is what gets sent. Across the browser's own DST boundary the two differ by an hour for the
   * one ambiguous slot; being faithful to the visible value is the better of the two wrong answers.
   */
  toUtc(wall: Date): Date {
    return new Date(wall.getTime() - this.offsetHours * 3_600_000 - wall.getTimezoneOffset() * 60_000)
  }

  /** "Now", on the client's clock, as a wall-clock Date — for today-markers and default slots. */
  nowWall(): Date {
    return this.toWall(new Date())
  }
}
